import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { LegacyRef } from 'react';

import { Match, PodsResponse, Timezone, Pod } from '../../types';

interface OfficialScoreSheetPrintProps {
	matches: Match[];
	innerRef?: LegacyRef<HTMLDivElement>;
	sheetSize?: 'Half Sheets' | 'Full Sheets';
	podsResponse?: PodsResponse;
	timeZone?: Timezone;
	teamOneTitle: string;
	teamTwoTitle: string;
}

interface ScoreTableProps {
	maxScore: number;
	showVwTwTf?: boolean;
	timeoutCount?: number;
}

interface ScoreRowProps extends ScoreTableProps {
	isFirstRow?: boolean;
}

interface GenericScoreTableProps {
	scoreFormatMaxGames: number;
	scoreFormatGameMaxValues: number[];
	timeoutCount?: number;
}

const getPlayers = (isDoubles: boolean, player1: string, player2?: string): (string | undefined)[] => (isDoubles ? [player1, player2] : [player1]);

const getScoreBoxWidth = (maxScore: number): string => {
	if (maxScore < 21) return 'min-w-8';
	if (maxScore >= 23) return 'min-w-[20px]';
	return 'min-w-6';
};

const getTimeoutHeaderWidth = (timeoutCount: number): string => {
	if (timeoutCount === 1) return 'w-[30px]';
	if (timeoutCount === 2) return 'w-[60px]';
	if (timeoutCount === 3) return 'w-[90px]';
	return 'w-[72px]';
};

const getBorderStyles = (isFirstRow: boolean, colIdx?: number) => ({
	borderTopWidth: isFirstRow ? '1px' : '0px',
	borderTopStyle: 'dashed' as const,
	borderBottomWidth: '1px',
	borderBottomStyle: 'dashed' as const,
	borderRightWidth: '1px',
	borderRightStyle: 'dashed' as const,
	borderLeftWidth: colIdx === 0 ? '1px' : '0px',
	borderLeftStyle: 'dashed' as const
});

const PlayerNamesWithMT = ({ players }: { players: (string | undefined)[] }) => {
	if (players.length === 2) {
		return (
			<div className="flex flex-row items-center gap-2">
				{players.map((name) => (
					<div key={name} className="flex flex-col">
						<div className="text-center min-w-44 border-b border-gray-900 font-bold text-xs mb-0.5 pb-0.5 min-h-4">{name}</div>
						<div className="flex items-center justify-center">
							<span className="text-xs border border-gray-900 p-0.5">MT</span>
						</div>
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="flex flex-col items-start">
			<div className="min-w-44 border-b border-gray-900 font-bold text-start text-xs mb-0.5 pb-0.5 min-h-4">{players[0]}</div>
			<div className="flex items-center gap-1 mb-1">
				<span className="text-[10px]">MT</span>
				<div className="h-3 w-3 border border-gray-900 ml-0.5 mr-0.5" />
			</div>
		</div>
	);
};

const MatchSeparator = () => (
	<div className="flex items-center justify-around my-3">
		<div className="text-xs text-gray-700">
			If above wins match,
			<br />
			all scores & initial
		</div>
		<div className="flex gap-2">
			{Array.from({ length: 4 }, (_, i) => (
				<div key={i} className="w-12 h-px bg-gray-900" />
			))}
		</div>
		<div className="text-xs text-gray-700 transform rotate-180">
			If above wins match,
			<br />
			all scores & initial
		</div>
	</div>
);

const ScoreTableHeader = ({ maxScore, showVwTwTf = false, timeoutCount = 0 }: ScoreTableProps) => (
	<div className="flex flex-row items-start mb-1">
		<div className="text-xs font-bold min-w-12 text-center">Server</div>
		<div className="flex flex-row items-start">
			{Array.from({ length: maxScore + 1 }, (_, colIdx) => (
				<div key={colIdx} className={clsx('min-h-4', getScoreBoxWidth(maxScore))} />
			))}
		</div>
		<div className="flex flex-row ml-4 gap-0">
			{timeoutCount > 0 && <div className={clsx('text-xs font-bold text-center', getTimeoutHeaderWidth(timeoutCount))}>T/O</div>}
			{showVwTwTf ? (
				<>
					<div className="w-[30px] text-xs font-bold text-center ml-4">VW</div>
					<span className="text-xs font-bold text-center"> / </span>
					<div className="w-[30px] text-xs font-bold text-center">TW</div>
					<span className="text-xs font-bold text-center"> / </span>
					<div className="w-[30px] text-xs font-bold text-center">TF</div>
				</>
			) : null}
		</div>
	</div>
);

const TimeoutBoxes = ({ timeoutCount, isFirstRow }: { timeoutCount: number; isFirstRow: boolean }) => (
	<div
		className="flex flex-row border-gray-400"
		style={{
			borderTopWidth: isFirstRow ? '1px' : '0px',
			borderTopStyle: 'solid'
		}}
	>
		{Array.from({ length: timeoutCount }, (_, idx) => (
			<div
				key={`timeout-${idx}`}
				className={clsx('border-b border-gray-400 w-[30px] h-[28px] flex items-center justify-center', {
					'border-l': idx === 0,
					'border-r': true
				})}
			/>
		))}
	</div>
);

const VwTwTfBoxes = () => (
	<div className="flex flex-row">
		{Array.from({ length: 4 }, (_, idx) => (
			<div
				key={`vw-tw-tf-${idx}`}
				className={clsx('border-t border-b border-gray-400 w-[30px] h-[28px] flex items-center justify-center', {
					'border-l border-r': idx === 0,
					'border-r': idx > 0
				})}
			/>
		))}
	</div>
);

const ScoreRow = ({ maxScore, showVwTwTf = false, isFirstRow = false, timeoutCount = 0 }: ScoreRowProps) => (
	<div className="flex flex-row items-center w-full">
		<div className="border-b border-gray-400 flex items-center justify-center min-h-7 min-w-12" />

		<div className="flex flex-row items-center">
			{Array.from({ length: maxScore + 1 }, (_, colIdx) => (
				<div
					key={colIdx}
					className={clsx('border-gray-400 flex items-center justify-center min-h-7', getScoreBoxWidth(maxScore))}
					style={getBorderStyles(isFirstRow, colIdx)}
				>
					<span className="text-sm font-medium">{colIdx}</span>
				</div>
			))}
		</div>

		<div className="flex flex-row ml-4 space-x-1">
			{timeoutCount > 0 && <TimeoutBoxes timeoutCount={timeoutCount} isFirstRow={isFirstRow} />}
			{showVwTwTf ? <VwTwTfBoxes /> : null}
		</div>
	</div>
);

const SwitchEndsTriangle = ({ maxScore }: { maxScore: number }) => {
	const switchEndsPosition = Math.ceil(maxScore / 2);

	return (
		<div className="flex flex-row items-center">
			<div className="min-w-12" />
			<div className="flex flex-row items-center">
				{Array.from({ length: maxScore + 1 }, (_, colIdx) => {
					const isSwitchEndsPosition = colIdx === switchEndsPosition;
					return (
						<div key={`triangle-${colIdx}`} className={clsx('h-4 flex items-center justify-center', getScoreBoxWidth(maxScore))}>
							{isSwitchEndsPosition ? (
								<div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[8px] border-l-transparent border-r-transparent border-b-gray-800" />
							) : null}
						</div>
					);
				})}
			</div>
		</div>
	);
};

const GenericScoreTable = ({ scoreFormatMaxGames, scoreFormatGameMaxValues, timeoutCount = 0 }: GenericScoreTableProps) => {
	const maxScore = scoreFormatGameMaxValues[0] ?? 11;

	return (
		<div className="flex flex-col my-4 w-full">
			<ScoreTableHeader maxScore={maxScore} showVwTwTf timeoutCount={timeoutCount} />
			{Array.from({ length: scoreFormatMaxGames }, (_, rowIdx) => {
				const isFirstRow = rowIdx === 0;
				return <ScoreRow key={rowIdx} maxScore={maxScore} showVwTwTf={isFirstRow} isFirstRow={isFirstRow} timeoutCount={timeoutCount} />;
			})}
			<SwitchEndsTriangle maxScore={maxScore} />
		</div>
	);
};

const MatchHeader = ({
	match,
	teamOnePlayers,
	podsResponse,
	timeZone,
	teamTwoTitle
}: {
	match: Match;
	teamOnePlayers: (string | undefined)[];
	podsResponse?: PodsResponse;
	timeZone?: Timezone;
	teamTwoTitle: string;
}) => {
	dayjs.extend(utc);
	dayjs.extend(timezone);

	let podTitle: string | undefined;
	let podNumber: string | undefined;
	let formattedPodTitle: string = '';

	if (podsResponse && podsResponse.pods) {
		podTitle = podsResponse.pods.find((pod: Pod) => pod.uuid === match.teamLeagueSeasonDivisionPodUuid)?.name;
		podNumber = podTitle?.split(' ').pop();
		formattedPodTitle = podNumber ? `P${podNumber}` : '';
	}
	const matchStartDateTime = dayjs.utc(match.matchPlannedStart).tz(timeZone?.ianaTzIdentifier).format('MMM D, YYYY | hh:mm A');

	return (
		<div className="flex justify-between items-start">
			<div className="flex flex-col justify-between h-20">
				<h2 className="text-base font-bold mb-0.5 min-w-[310px] border-b border-gray-900">
					{formattedPodTitle} {match.roundText}
				</h2>
				<div className="flex flex-col">
					<p className="text-sm font-semibold text-gray-700  min-w-[310px] border-b border-gray-900 mb-1">{teamTwoTitle}</p>
					<PlayerNamesWithMT players={teamOnePlayers} />
				</div>
			</div>
			<div className="flex flex-col items-end gap-2">
				<div className="flex flex-row gap-1">
					<p className="text-xs">Time:</p>
					<span className="min-w-44 border-b border-gray-900 text-xs font-bold">{matchStartDateTime}</span>
				</div>
				<div className="flex flex-row gap-1">
					<p className="text-xs">Court:</p>
					<span className="min-w-44 border-b border-gray-900 text-xs font-bold">{match.courtTitle || ''}</span>
				</div>
				<div className="flex flex-row gap-1">
					<p className="text-xs">Referee:</p>
					<span className="min-w-44 border-b border-gray-900 text-xs font-bold" />
				</div>
				<div className="flex flex-row gap-1">
					<p className="text-xs">Format:</p>
					<span className="min-w-44 border-b border-gray-900 text-xs font-bold">{match.scoreFormatShorthand}</span>
				</div>
			</div>
		</div>
	);
};

const MatchSheet = ({
	match,
	idx,
	isHalfSheet,
	podsResponse,
	timeZone,
	teamOneTitle,
	teamTwoTitle
}: {
	match: Match;
	idx: number;
	isHalfSheet: boolean;
	podsResponse?: PodsResponse;
	timeZone?: Timezone;
	teamOneTitle: string;
	teamTwoTitle: string;
}) => {
	const scoreFormatGameMaxValues = [
		match.scoreFormatGame1Max,
		match.scoreFormatGame2Max,
		match.scoreFormatGame3Max,
		match.scoreFormatGame4Max,
		match.scoreFormatGame5Max
	].filter((v): v is number => typeof v === 'number');

	const isDoubles = match.formatTitle.toLowerCase().includes('doubles');
	const teamOnePlayers = getPlayers(isDoubles, match.teamTwoPlayerOneName ?? '', match.teamTwoPlayerTwoName ?? '');
	const teamTwoPlayers = getPlayers(isDoubles, match.teamOnePlayerOneName ?? '', match.teamOnePlayerTwoName ?? '');
	const timeoutCount = match.timeoutCountTeamOneGameOne || 0;

	return (
		<div
			key={match.matchUuid || idx}
			className={clsx({
				'print-half-sheet-official': isHalfSheet,
				'print-full-sheet-official': !isHalfSheet
			})}
		>
			<div>
				<MatchHeader
					match={match}
					teamOnePlayers={teamOnePlayers}
					podsResponse={podsResponse}
					timeZone={timeZone}
					teamTwoTitle={teamTwoTitle}
				/>

				<GenericScoreTable
					scoreFormatMaxGames={match.scoreFormatMaxGames ?? 1}
					scoreFormatGameMaxValues={scoreFormatGameMaxValues}
					timeoutCount={timeoutCount}
				/>

				<MatchSeparator />

				<div className="mt-4 rotate-180">
					<div className="flex flex-row justify-start items-start gap-8 mb-0.5 mt-2">
						<div className="flex flex-col items-start min-w-[120px]">
							<p className="text-sm font-semibold text-gray-700 min-w-[310px] border-b border-gray-900 mb-1">{teamOneTitle}</p>
							<PlayerNamesWithMT players={teamTwoPlayers} />
						</div>
					</div>
					<GenericScoreTable
						scoreFormatMaxGames={match.scoreFormatMaxGames ?? 1}
						scoreFormatGameMaxValues={scoreFormatGameMaxValues}
						timeoutCount={timeoutCount}
					/>
				</div>
			</div>
		</div>
	);
};

export const OfficialScoreSheetPrint = ({
	matches,
	innerRef,
	sheetSize = 'Half Sheets',
	podsResponse,
	timeZone,
	teamOneTitle,
	teamTwoTitle
}: OfficialScoreSheetPrintProps) => {
	const isHalfSheet = sheetSize === 'Half Sheets';

	return (
		<div ref={innerRef} className="mx-0 p-0 print:block hidden print-container">
			{/* NOTE: temporary sorting matches by court title, we need better sorting logic. see ticket https://pickleball-product-team.atlassian.net/browse/PW-3969 */}
			{[...matches]
				.sort((a, b) => {
					const getCourtNumber = (title: string | null | undefined) => {
						const num = title ? parseInt(title, 10) : NaN;
						return isNaN(num) ? null : num;
					};

					const numA = getCourtNumber(a.courtTitle);
					const numB = getCourtNumber(b.courtTitle);

					// Numbers first, then alphabetical
					if (numA !== null && numB !== null) return numA - numB;
					if (numA !== null) return -1;
					if (numB !== null) return 1;
					return (a.courtTitle || '').localeCompare(b.courtTitle || '');
				})
				.map((match, idx) => (
					<MatchSheet
						key={match.matchUuid || idx}
						match={match}
						idx={idx}
						isHalfSheet={isHalfSheet}
						podsResponse={podsResponse}
						timeZone={timeZone}
						teamOneTitle={teamOneTitle}
						teamTwoTitle={teamTwoTitle}
					/>
				))}
		</div>
	);
};
